import { memo, useState, useMemo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Divider from 'LuxKit/Divider'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import Expander from 'components/utils/Expander'
import { take } from 'lib/array/arrayUtils'
import CommentItem from './CommentItem'
import { COMMENT_MAX_VISIBLE } from 'constants/comments'

const CommentWrapper = styled.div`
  position: relative;
  width: 100%;
`

const StyledDivider = styled(Divider)`
  margin: ${rem(12)} 0;
`

const StyledTextLink = styled(TextLink)`
  margin-top: ${rem(8)};
`

const StyledExpander = styled(Expander)`
  width: 100%;
`

interface Props {
  comments: Array<App.MultiCartComment>
  currentUserId?: string
  editingCommentId?: string
  onEdit: (comment: App.MultiCartComment) => void
  onDelete: (comment: App.MultiCartComment) => void
  onUpdateComment: (commentId: string, text: string) => void
  onCancelEdit: (commentId: string) => void
  onEmptyEdit: (comment: App.MultiCartComment) => void
  showLeadingDivider?: boolean
}

const CommentItemMemo = memo(CommentItem)

export default function CommentList({
  comments,
  currentUserId,
  editingCommentId,
  onEdit,
  onDelete,
  onUpdateComment,
  onCancelEdit,
  onEmptyEdit,
  showLeadingDivider = false,
}: Props) {
  const intl = useIntl()
  const [showAll, setShowAll] = useState(false)

  const handleToggleShowAll = useCallback(() => {
    setShowAll(prev => !prev)
  }, [])

  // Sort comments by creation date (latest first)
  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
  }, [comments])

  const initialComments = useMemo(() => take(sortedComments, COMMENT_MAX_VISIBLE), [sortedComments])
  const remainingComments = useMemo(() => sortedComments.slice(COMMENT_MAX_VISIBLE), [sortedComments])
  const hasMore = sortedComments.length > COMMENT_MAX_VISIBLE
  const totalComments = sortedComments.length

  if (comments.length === 0) {
    return null
  }

  return (
    <Group direction="vertical">
      {initialComments.map((comment, index) => (
        <CommentWrapper key={comment.id}>
          {(index > 0 || showLeadingDivider) && <StyledDivider kind="secondary" />}
          <CommentItemMemo
            comment={comment}
            currentUserId={currentUserId}
            isEditing={editingCommentId === comment.id}
            onEdit={onEdit}
            onDelete={onDelete}
            onUpdateComment={onUpdateComment}
            onCancelEdit={onCancelEdit}
            onEmptyEdit={onEmptyEdit}
          />
        </CommentWrapper>
      ))}
      {hasMore && (
        <>
          <StyledExpander expanded={showAll} contentTransition="default">
            {remainingComments.map((comment) => (
              <CommentWrapper key={comment.id}>
                <StyledDivider kind="secondary" />
                <CommentItemMemo
                  comment={comment}
                  currentUserId={currentUserId}
                  isEditing={editingCommentId === comment.id}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onUpdateComment={onUpdateComment}
                  onCancelEdit={onCancelEdit}
                  onEmptyEdit={onEmptyEdit}
                />
              </CommentWrapper>
            ))}
          </StyledExpander>
          <StyledTextLink
            weight="bold"
            underline
            size="medium"
            onClick={handleToggleShowAll}
          >
            {showAll ? intl.formatMessage({ id: 'nP88ZcNQevo9XNLHG30d', defaultMessage: 'Show fewer comments', description: 'Link to collapse and show fewer comments in the shopping cart' }) : intl.formatMessage({ id: '0pgrgtowXGzQf-FJrrv0', defaultMessage: 'View all {count} comments', description: 'Hyperlink text to expand and view all comments in the shopping cart' }, { count: totalComments })}
          </StyledTextLink>
        </>
      )}
    </Group>
  )
}
